import { Close, ToggleOnOutlined, WorkspacesOutlined } from "@mui/icons-material";
import { Box, ButtonGroup, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Divider, Grid2, IconButton, Skeleton, Stack, Typography } from "@mui/material";
import Collapse from "@mui/material/Collapse";
import { BillingPeriodSelect } from "components/BillingPeriodSelect";
import { CommunitySharingTypeSelect, IndieDSOIDField } from "components/dialogs/communities/NewCommunityDialog";
import BannerImageUpload from "components/dialogs/physical-units/BannerImageUpload";
import CommunityStatusSelect from "components/forms/communities/CommunityStatusSelect";
import { CommunityTypeSelect } from "components/forms/communities/CommunityTypeSelector";
import EntityFormFields from "components/forms/entities/EntityFormFields";
import IndieButton from "components/forms/IndieButton";
import IndieCancelButton from "components/forms/IndieCancelButton";
import IndieContinueButton from "components/forms/IndieContinueButton";
import IndieDesktopDatePicker from "components/forms/IndieDesktopDatePickerV2";
import IndieEditButton from "components/forms/IndieEditButton";
import IndieSaveButton from "components/forms/IndieSaveButton";
import IndieTextField from "components/forms/IndieTextField";
import IndieUpdateStatusButton from "components/forms/IndieUpdateStatusButton";
import IndiePaymentTermsSelect from "components/forms/tariffs/IndiePaymentTermsSelect";
import IndieReceiptTermsSelect from "components/forms/tariffs/IndieReceiptTermsSelect";
import IndieOutlinedCardSection from "components/IndieOutlinedCardSection";
import PhysicalUnitSelect from "components/markets/PhysicalUnitSelect";
import SelectYesNo from "components/SelectYesNo";
import { DRF_DATE_FORMAT } from "constants";
import { CommunityContext } from "contexts/communities/CommunityContext";
import { TariffsContextProvider } from "contexts/tariffs/TariffsContext";
import { UserFeedbackContext } from "contexts/UserFeedbackContext";
import { Form, Formik } from "formik";
import useAuth from "hooks/useAuth";
import _ from "lodash";
import moment from "moment";
import { Fragment, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { validateCPE } from "utils/utils";
import * as Yup from "yup";

const CommunityDetailsTab = () => {

    const { t } = useTranslation();
    const [initializing, setInitializing] = useState(true);
    const { community, updateCommunity, activateCommunityManagement, submitting, getCommunitiesTariffModels, updateBannerImage, deleteBannerImage } = useContext(CommunityContext);
    const [updatingCommunity, setUpdatingCommunity] = useState(false);
    const [initialValues, setInitialValues] = useState(community);
    const [editMode, setEditMode] = useState(false);
    const [communityTariffModels, setCommunityTariffModels] = useState([]);
    const [openCommunityStatusDialog, setOpenCommunityStatusDialog] = useState(false);
    const [openActivateCommunityManagementDialog, setOpenActivateCommunityManagementDialog] = useState(false);
    const [openCommunityManagementActivationDialog, setOpenCommunityManagementActivationDialog] = useState(false);
    const { setAndShowErrorMessage } = useContext(UserFeedbackContext);
    const { hasPermissionGroup } = useAuth();

    const validationSchema = Yup.object().shape({
        name: Yup.string().required(t("error.forms.mandatory")),
        dso_id: Yup.number().required(t("error.forms.mandatory")),
        type: Yup.string().required(t("error.forms.mandatory")),
        agent_entity: Yup.object().required(t("error.forms.mandatory")),
        aggregator_deliverypoint: Yup.string().notRequired().test('isValidCPE', t("error.forms.invalidCPE"), validateCPE)
    });

    const handleCancelUpdateStatusDialog = () => {
        setOpenCommunityStatusDialog(false);
    }

    const handleSuccessUpdateStatusDialog = (status) => {
        setOpenCommunityStatusDialog(false);
    }

    const onConfirmActivateCommunityManagement = () => {
        activateCommunityManagement(community.agent_entity.id).then(() => {
            setOpenActivateCommunityManagementDialog(false);
            setOpenCommunityManagementActivationDialog(true);
        }).catch((e) => {
            setOpenActivateCommunityManagementDialog(false);
            setAndShowErrorMessage(e);
        });
    }

    const onCancelCActivateCommunityManagement = () => {
        setOpenActivateCommunityManagementDialog(false);
    }

    const onConfirmCommunityManagementActivation = () => {
        setOpenCommunityManagementActivationDialog(false);
    }

    const handleSubmit = (values) => {
        const _values = _.cloneDeep(values);

        if (_values['banner_image'] instanceof File) {
            updateBannerImage(_values['banner_image']);
        } else if (_values['banner_image'] === null) {
            deleteBannerImage();
        }

        const rawDate = _values['activation_date'];
        _values['activation_date'] = rawDate ? moment(rawDate).format(DRF_DATE_FORMAT) : null;

        updateCommunity(_values).then(() => {
            setEditMode(false);
        }).catch((e) => {
            setAndShowErrorMessage(e);
        });
    }

    const activateEditMode = () => {
        setEditMode(true);
    };

    const cancelEditMode = (formik) => {
        setEditMode(false);
        formik.resetForm();
    };

    const isActivationDateDisabled = !editMode || !["active", "suspended"].includes(community.status);

    useEffect(() => {
        setUpdatingCommunity(true);
        setInitialValues(community);
        setUpdatingCommunity(false);
        setCommunityTariffModels(getCommunitiesTariffModels().then((response) => {
            setCommunityTariffModels(response.data.map((m) => ({ value: m.id, label: t(`label.communities.tariffModel.title.${m.code}`) })));
            setInitializing(false);
        }).catch((error) => {
            setAndShowErrorMessage(error);
            setCommunityTariffModels([]);
            setInitializing(false);
        }));
    }, [community]);

    if (updatingCommunity || initializing) {
        return (
            <Stack spacing={1}>
                <Skeleton variant="rectangular" height={300} />
                <Skeleton variant="rectangular" height={300} />
                <Skeleton variant="rectangular" height={300} />
            </Stack>
        )
    }

    return (
        <TariffsContextProvider>
            <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                validateOnBlur={false}
                validateOnChange={false}
                onSubmit={handleSubmit}
                enableReinitialize
            >
                {(formik) => (
                    <Form>
                        <Stack spacing={2}>
                            <Stack direction="row" spacing={1}>
                                <ButtonGroup variant="outlined">
                                    <IndieEditButton id="button_edit_producer" variant="outlined" disabled={editMode || submitting} onClick={() => activateEditMode()} />
                                    <IndieSaveButton id="button_save_producer" color="primary" disabled={!editMode} variant="outlined" type="submit" size="small" loading={submitting} />
                                    <IndieCancelButton id="button_cancel_edit_producer" color="error" disabled={!editMode} variant="outlined" onClick={() => cancelEditMode(formik)} size="small" />
                                </ButtonGroup>
                                {hasPermissionGroup("ENERGY_AGENT_EGAC_USER") && (
                                    <>
                                        <IndieUpdateStatusButton id="update_status" disabled={submitting} onClick={() => setOpenCommunityStatusDialog(true)} />
                                        {!community.managing_entity && (
                                            <IndieButton id="activate_community_management" startIcon={<ToggleOnOutlined />} start disabled={submitting} onClick={() => setOpenActivateCommunityManagementDialog(true)} variant="outlined">
                                                {t("communityDetails.activateCommunityManagement")}
                                            </IndieButton>
                                        )}
                                    </>
                                )}
                            </Stack>
                            <Grid2 container spacing={2}>
                                <Grid2 item size={{ xs: 12, sm: 6, md: hasPermissionGroup("ENERGY_AGENT_EGAC_USER") ? 4 : 6 }}>
                                    <IndieOutlinedCardSection title="label.title.communityDetails">
                                        <IndieTextField id="name" name="name" label={t("label.form.name")} disabled={!editMode} />
                                        {hasPermissionGroup("ENERGY_AGENT_EGAC_USER") && (
                                            <IndieDesktopDatePicker id="activation_date" name="activation_date" label={t("label.form.activationDate")} disabled={isActivationDateDisabled || !community.managing_entity} />
                                        )}
                                        <IndieDSOIDField id="dso_id" name="dso_id" label={t("label.form.code")} type={formik.values?.type} disabled={!editMode} />
                                    </IndieOutlinedCardSection>
                                </Grid2>
                                <Grid2 item size={{ xs: 12, sm: 6, md: hasPermissionGroup("ENERGY_AGENT_EGAC_USER") ? 4 : 6 }}>
                                    <IndieOutlinedCardSection title="label.title.type">
                                        <Stack spacing={2}>
                                            <CommunityTypeSelect id="type" name="type" disabled={!editMode} />
                                            {hasPermissionGroup("ENERGY_AGENT_EGAC_USER") && (
                                                <CommunitySharingTypeSelect id="sharing_type" name="sharing_type" disabled={!editMode || !community.managing_entity} />
                                            )}
                                        </Stack>
                                    </IndieOutlinedCardSection>
                                </Grid2>
                                {hasPermissionGroup("ENERGY_AGENT_EGAC_USER") && (
                                    <Grid2 item size={{ xs: 12, sm: 6, md: 4 }}>
                                        <IndieOutlinedCardSection title="label.title.invoicing">
                                            <Stack spacing={2}>
                                                <SelectYesNo id="invoice_network_costs" name="invoice_network_costs" label={t("label.form.invoiceNetworkCosts")} disabled={!editMode || !community.managing_entity} />
                                                <BillingPeriodSelect
                                                    id="billing_period" name="billing_period"
                                                    receipt={true}
                                                    label={t("label.form.billingPeriod")}
                                                    agent={community?.agent.id}
                                                    disabled={!editMode || !community.managing_entity}
                                                />
                                                <IndiePaymentTermsSelect id="sale_payment_terms" name="sale_payment_terms" label={t("label.form.paymentTerms")} disabled={!editMode || !community.managing_entity} agent={community?.agent.id} />
                                                <IndieReceiptTermsSelect id="purchase_payment_terms" name="purchase_payment_terms" label={t("label.form.receiptTerms")} disabled={!editMode || !community.managing_entity} agent={community?.agent.id} />
                                            </Stack>
                                        </IndieOutlinedCardSection>
                                    </Grid2>
                                )}
                                <Grid2 item size={{ xs: 12, sm: 6, md: 6 }}>
                                    <IndieOutlinedCardSection title="newAgentCommunity.egacRegisteredDGEG">
                                        <Stack spacing={2}>
                                            <IndieTextField id="dgeg_name" name="dgeg_name" label={t("label.form.name")} disabled={!editMode} />
                                            <IndieTextField id="dgeg_fiscal_number" name="dgeg_fiscal_number" label={t("label.form.NIF")} disabled={!editMode} />
                                        </Stack>
                                    </IndieOutlinedCardSection>
                                </Grid2>
                                <Grid2 item size={{ xs: 12, sm: 6, md: 6 }}>
                                    <IndieOutlinedCardSection title="newAgentCommunity.registrationData">
                                        <Stack spacing={2}>
                                            <IndieTextField id="aggregator_deliverypoint" name="aggregator_deliverypoint" label={t("aggregatorDeliverypoint")} disabled={!editMode} />
                                            <IndieTextField id="certified_power" name="certified_power" label={t("label.form.certifiedPower")} disabled={!editMode} />
                                            <IndieTextField id="installed_power" name="installed_power" label={t("label.form.installedPowerKWh")} disabled={!editMode} />
                                            <IndieTextField id="storage_total" name="storage_total" label={t("label.form.storageCapacityKWhTotal")} disabled={!editMode} />
                                            <PhysicalUnitSelect
                                                id="trading_physical_unit"
                                                name="trading_physical_unit"
                                                label={t("label.form.gcsCode")}
                                                connectionPower={community.installed_power}
                                                handleOnChange={(e, v) => formik.setFieldValue("trading_physical_unit", v?.id)}
                                                noInstallation
                                                disabled={!editMode}
                                                agent={community.agent.id}
                                            />
                                        </Stack>
                                    </IndieOutlinedCardSection>
                                </Grid2>
                                <Grid2 item size={{ xs: 12, sm: 6, md: 12 }}>
                                    <IndieOutlinedCardSection title="communities.communityImage">
                                        <Stack spacing={2}>
                                            <Box maxWidth={"md"}>
                                                <CommunityBannerUpload formDisabled={!editMode} />
                                            </Box>
                                        </Stack>
                                    </IndieOutlinedCardSection>
                                </Grid2>
                                {hasPermissionGroup("ENERGY_AGENT_EGAC_USER") && (
                                    <Grid2 item size={{ xs: 12 }}>
                                        <IndieOutlinedCardSection title="label.form.managingEntity">
                                            <EntityFormFields prefix={"managing_entity"} disabled manual />
                                        </IndieOutlinedCardSection>
                                    </Grid2>
                                )}
                            </Grid2>
                        </Stack>
                        <CommunityUpdateStatusDialog open={openCommunityStatusDialog} handleClose={handleCancelUpdateStatusDialog} handleSuccess={handleSuccessUpdateStatusDialog} />
                        <ActivateCommunityManagementDialog
                            open={openActivateCommunityManagementDialog}
                            onConfirm={onConfirmActivateCommunityManagement}
                            onClose={onCancelCActivateCommunityManagement}
                        />
                        <CommunityManagementActivationDialog
                            open={openCommunityManagementActivationDialog}
                            onConfirm={onConfirmCommunityManagementActivation}
                        />
                    </Form>
                )}
            </Formik>
        </TariffsContextProvider>
    )
}

const ActivateCommunityManagementDialog = ({ open, onConfirm, onClose }) => {
    const { t } = useTranslation();
    const { community } = useContext(CommunityContext);

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle>
                <Stack spacing={2} direction="row" alignItems="center">
                    <ToggleOnOutlined color="primary" />
                    <Typography variant="subtitle1" color="textPrimary">{t("communityDetails.activateCommunityManagement")}</Typography>
                </Stack>
            </DialogTitle>
            <DialogContent>
                <Stack spacing={2}>
                    <Divider />
                    <DialogContentText>{t("communityDetails.activateCommunityManagement.confirmText", { agentName: community.agent_entity.name })}</DialogContentText>
                </Stack>
            </DialogContent>
            <DialogActions>
                <IndieCancelButton label={t("label.form.no")} onClick={onClose} />
                <IndieContinueButton label={t("label.form.yes")} onClick={onConfirm} autoFocus />
            </DialogActions>
        </Dialog>
    )
}

const CommunityManagementActivationDialog = ({ open, onConfirm }) => {
    const { t } = useTranslation();
    const { community } = useContext(CommunityContext);

    return (
        <Dialog open={open} onClose={onConfirm} fullWidth maxWidth="md">
            <DialogTitle>
                <Stack spacing={2} direction="row" alignItems="center">
                    <ToggleOnOutlined color="primary" />
                    <Typography variant="subtitle1" color="textPrimary">{t("communityDetails.activateCommunityManagement")}</Typography>
                </Stack>
            </DialogTitle>
            <DialogContent>
                <Stack spacing={2}>
                    <Divider />
                    <DialogContentText>{t("communityDetails.activateCommunityManagement.activation", { communityName: community.name })}</DialogContentText>
                </Stack>
            </DialogContent>
            <DialogActions>
                <IndieContinueButton label={t("label.button.continue")} onClick={onConfirm} autoFocus />
            </DialogActions>
        </Dialog>
    )
}

const CommunityBannerUpload = ({ formDisabled }) => {
    const { community } = useContext(CommunityContext);

    return (
        <Fragment>
            <Stack spacing={2}>
                <BannerImageUpload fieldName="banner_image" isFormDisabled={formDisabled} imageSrc={community.banner_image} />
            </Stack>
        </Fragment>
    )
}

const CommunityUpdateStatusDialog = ({ open, handleClose, handleSuccess }) => {

    const { community, updating, updateStatus } = useContext(CommunityContext);
    const { setAndShowErrorMessage, setAndShowSuccessMessage } = useContext(UserFeedbackContext);
    const { t } = useTranslation();

    return (

        <Dialog open={open} maxWidth="lg" fullWidth>
            <Formik
                initialValues={{ status: community.status, activationDate: community.activation_date }}
                validateOnBlur={false}
                validateOnChange={false}
                validationSchema={Yup.object().shape({
                    status: Yup.string().required(t("error.forms.mandatory")),
                    activationDate: Yup.date()
                        .nullable()
                        .when("status", {
                            is: (status) => status === "active" || status === "suspended",
                            then: schema => schema.required(t("error.forms.mandatory")),
                            otherwise: schema => schema.notRequired(),
                        }),
                })}
                onSubmit={(values) => {
                    const rawDate = values.activationDate;
                    const activationDate = rawDate ? moment(rawDate).format(DRF_DATE_FORMAT) : null;

                    updateStatus(values.status, activationDate).then(() => {
                        handleSuccess(values.status);
                        setAndShowSuccessMessage("success.api.save");
                    }).catch((error) => {
                        setAndShowErrorMessage(error);
                    });
                }}
            >
                {(formik) => (
                    <Form>
                        <DialogTitle>
                            <Stack spacing={2} direction="row" alignItems="center">
                                <IconButton onClick={handleClose}>
                                    <Close />
                                </IconButton>
                                <WorkspacesOutlined color="primary" />
                                <Typography variant="subtitle1" color="textPrimary">{t("label.button.updateStatus")}</Typography>
                            </Stack>
                        </DialogTitle>
                        <DialogContent>
                            <Stack spacing={2}>
                                <Divider />
                                <CommunityStatusSelect id="status" name="status" disabled={updating} />
                                <Collapse in={formik.values.status === "active" || formik.values.status === "suspended"}>
                                    <IndieDesktopDatePicker id="activation_date" name="activationDate" label={t("label.form.activationDate")} disabled={updating} />
                                </Collapse>
                            </Stack>
                        </DialogContent>
                        <DialogActions>
                            <IndieCancelButton onClick={handleClose} disabled={updating} />
                            <IndieSaveButton type="submit" loading={updating} disabled={updating} />
                        </DialogActions>
                    </Form>
                )}
            </Formik>
        </Dialog >
    )
}


export default CommunityDetailsTab;